import { Trans } from 'react-i18next';
import PhoneNumber from 'flow/components/ui-components/PhoneNumber';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getExtensionContactFirstNameLastName } from 'flow/selectors/extension-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { CallRecord } from 'flow/types/CallRecord';
import { TypedMap } from 'flow/types/TypedMap';
import { PageType } from 'flow/components/call-history-v2/types';
import { getDateOfPersonalCallRecord } from 'flow/utils/call-records-utils';
import { getDateDescription } from 'flow/utils';

interface Props {
    callRecord: TypedMap<CallRecord>;
    duration: number;
    pageType?: PageType;
}

const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const TranscriptionMetadata = ({ callRecord, duration, pageType }: Props) => {
    const { i18next } = useAppContext();
    const date = getDateDescription(
        getDateOfPersonalCallRecord(callRecord),
        i18next,
        true,
    );

    const agentKey = callRecord.getIn(['agent', 'key']);
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const agentIsOwnUser = agentKey === ownExtensionKey;
    const agentExtensionName = useAppSelector((state) =>
        agentKey
            ? getExtensionContactFirstNameLastName(state, agentKey).trim()
            : '',
    );

    const otherContact = callRecord.get('contact');
    const otherFirstName = otherContact?.get('firstName') ?? '';
    const otherLastName = otherContact?.get('lastName') ?? '';
    const otherName = `${otherFirstName} ${otherLastName}`.trim();
    const otherNumber = callRecord.getIn(['number', 'number']);

    const otherNode = otherName || <PhoneNumber number={otherNumber} />;

    const direction = callRecord.get('direction');
    const isPersonalPage = pageType === 'personal';

    const renderDescription = () => {
        if (isPersonalPage) {
            const key =
                direction === 'incoming'
                    ? 'transcription.metadata.description.personal.incoming'
                    : 'transcription.metadata.description.personal.outgoing';
            return (
                <Trans
                    i18n={i18next}
                    i18nKey={key}
                    components={[<span key="other">{otherNode}</span>]}
                    values={{ date }}
                />
            );
        }

        if (agentIsOwnUser) {
            const key =
                direction === 'incoming'
                    ? 'transcription.metadata.description.user.incoming'
                    : 'transcription.metadata.description.user.outgoing';
            return (
                <Trans
                    i18n={i18next}
                    i18nKey={key}
                    components={[<span key="other">{otherNode}</span>]}
                    values={{ date }}
                />
            );
        }

        const nonSelfAgentNode =
            agentExtensionName || i18next.t('Contact.Unknown');
        const callerNode =
            direction === 'incoming' ? otherNode : nonSelfAgentNode;
        const calleeNode =
            direction === 'incoming' ? nonSelfAgentNode : otherNode;
        return (
            <Trans
                i18n={i18next}
                i18nKey="transcription.metadata.description"
                components={[
                    <span key="tm-caller">{callerNode}</span>,
                    <span key="tm-callee">{calleeNode}</span>,
                ]}
                values={{ date }}
            />
        );
    };

    return (
        <div className="text-sm text-gray-500">
            {date && <div>{renderDescription()}</div>}
            <div>
                {i18next.t('transcription.metadata.duration', {
                    duration: formatDuration(duration),
                })}
            </div>
        </div>
    );
};

export default TranscriptionMetadata;
